import { useEffect, useMemo, useRef, useState } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import { DIFFICULTY, sideSequence } from "../../engine/StepEngine";
import { Seed, Burst } from "./Seed";
import { Panel } from "./Panel";

const AMP = { doux: 0.45, moyen: 0.7, fort: 0.9 };
// Reach directions (right arm; mirrored for left), relative to the shoulder: forward, high-forward, side, cross-body, high-side.
const DIRS = [[0.15, 0, -1], [0.1, 0.55, -0.85], [0.8, 0.05, -0.6], [-0.45, 0.1, -0.9], [0.6, 0.6, -0.55]];
const SEATED_HEAD = new THREE.Vector3(0, 1.18, 0);
const LINES = {
  intro: "Bonjour Bao ! Assieds-toi confortablement. Tends doucement le bras vers chaque Graine de Vie dorée et touche-la avec ta manette. Prends tout ton temps.",
  done: "Merveilleux ! Le Grand Baobab a bu toutes tes graines. Tes résultats sont envoyés à ton kiné.",
};

/** Builds the seed layout in front of the seated patient from the head pose at start. */
const layout = (head, diff, affected, share) => {
  const n = DIFFICULTY[diff].reps * 3, sides = sideSequence(n, affected, share), reach = 0.3 + 0.32 * AMP[diff];
  return sides.map((side, i) => {
    const [x, y, z] = DIRS[i % DIRS.length], m = side === "left" ? -1 : 1;
    const shoulder = head.clone().add(new THREE.Vector3(0.18 * m, -0.3, 0));
    return { side, dist: reach, pos: shoulder.add(new THREE.Vector3(x * m, y, z).normalize().multiplyScalar(reach)) };
  });
};

const emptySide = () => ({ attempts: 0, successes: 0, reaches: [] });

/** The VR reach exercise: one glowing seed at a time, collected by touching it with the controller (grip) — or clicking it in the desktop preview. */
export const SeedGame = ({ ctx, diff, onDone, onPhase }) => {
  const { gl, camera } = useThree();
  const [phase, setPhase] = useState("intro"), [i, setI] = useState(0), [bursts, setBursts] = useState([]);
  const targets = useRef([]), m = useRef(null), cur = useRef(null), head0 = useRef(new THREE.Vector3());
  const tmp = useMemo(() => new THREE.Vector3(), []), hands = useRef({}), ir = useRef(0);
  useEffect(() => onPhase?.(phase, i), [phase, i, onPhase]);
  // Desktop preview only: Space bar = simulated reach (keyboard), so therapists can demo without a headset.
  useEffect(() => {
    const k = (e) => { if (e.code === "Space" && phase === "play" && !gl.xr.isPresenting && targets.current[ir.current]) { e.preventDefault(); collect(targets.current[ir.current].side, "keyboard"); } };
    window.addEventListener("keydown", k); return () => window.removeEventListener("keydown", k);
  });

  const start = () => {
    camera.getWorldPosition(head0.current);
    if (!gl.xr.isPresenting) head0.current.copy(SEATED_HEAD);
    targets.current = layout(head0.current, diff, ctx.affected, ctx.share);
    m.current = { sides: { left: emptySide(), right: emptySide() }, stableS: 0, totalS: 0, smooth: 0, firstTry: 0, t0: performance.now() };
    cur.current = { born: performance.now(), peakV: 0 };
    ir.current = 0; setI(0); setPhase("play");
  };

  const collect = (side, how) => {
    const t = targets.current[ir.current], s = m.current.sides[t.side], dt = (performance.now() - cur.current.born) / 1000;
    s.attempts += 1; s.successes += 1; s.reaches.push({ dt, dist: t.dist, hand: side, how });
    if (cur.current.peakV < 1.2) m.current.smooth += 1;
    if (dt < 8) m.current.firstTry += 1;
    setBursts((b) => [...b.slice(-3), { at: t.pos.clone(), born: performance.now(), id: Math.random() }]);
    next();
  };
  const next = () => {
    ir.current += 1;
    if (ir.current >= targets.current.length) { setPhase("done"); onDone(summary(m.current, targets.current.length, ctx, diff)); return; }
    cur.current = { born: performance.now(), peakV: 0 }; setI(ir.current);
  };

  useFrame((state, dt, frame) => {
    const t = targets.current[ir.current];
    if (phase !== "play" || !t) return;
    m.current.totalS += dt;
    if (gl.xr.isPresenting && frame) {
      camera.getWorldPosition(tmp);
      if (tmp.distanceTo(head0.current) < 0.08) m.current.stableS += dt;
      const ref = gl.xr.getReferenceSpace();
      for (const src of frame.session.inputSources) {
        const pose = src.gripSpace && frame.getPose(src.gripSpace, ref);
        if (!pose) continue;
        const p = pose.transform.position, v = new THREE.Vector3(p.x, p.y, p.z), prev = hands.current[src.handedness];
        if (prev && dt > 0) cur.current.peakV = Math.max(cur.current.peakV, prev.distanceTo(v) / dt);
        hands.current[src.handedness] = v;
        if (v.distanceTo(t.pos) < 0.1) { collect(src.handedness, "webxr"); return; }
      }
    } else m.current.stableS += dt;
    if ((performance.now() - cur.current.born) / 1000 > 15) { m.current.sides[t.side].attempts += 1; next(); }
  });

  const t = targets.current[i];
  return (
    <group>
      {phase === "intro" && <Panel testId="xr-panel-intro" position={[0, 1.45, -1.5]} kicker="Mama Naya · Le Village" title="La cueillette des graines" body={LINES.intro} button="Commencer" onButton={start} />}
      {phase === "play" && t && <Seed key={i} position={t.pos} side={t.side} onPick={() => collect(t.side, "pointer")} testId={`xr-seed-${i}`} />}
      {phase === "play" && <Panel testId="xr-panel-progress" position={[0, 1.85, -1.9]} width={1} kicker={`Bras ${t?.side === "left" ? "gauche" : "droit"}`} title={`Graine ${i + 1} / ${targets.current.length}`} body="Tends le bras doucement, sans te pencher." />}
      {phase === "done" && <Panel testId="xr-panel-done" position={[0, 1.45, -1.5]} kicker="Bravo !" title="Séance réussie" body={LINES.done} />}
      {bursts.map((b) => <Burst key={b.id} at={b.at} born={b.born} />)}
    </group>
  );
};

const summary = (mm, n, ctx, diff) => {
  const sides = Object.fromEntries(["left", "right"].map((k) => {
    const s = mm.sides[k], amp = s.reaches.length ? Math.min(100, (s.reaches.reduce((a, r) => a + r.dist, 0) / s.reaches.length / 0.62) * 100) : 0;
    return [k, { attempts: s.attempts, successes: s.successes, amplitude_pct: Math.round(amp * 10) / 10, balance_s: 0 }];
  }));
  const all = [...mm.sides.left.reaches, ...mm.sides.right.reaches], ok = all.length;
  const attempts = mm.sides.left.attempts + mm.sides.right.attempts;
  return {
    session_id: ctx.sessionId || null, level: "village", goals: {}, difficulty: diff, affected_side: ctx.affected, affected_share: ctx.share, seeds: ok,
    precision: attempts ? Math.round((mm.firstTry / attempts) * 1000) / 10 : 0, reaction_s: ok ? Math.round((all.reduce((a, r) => a + r.dt, 0) / ok) * 100) / 100 : 0,
    amplitude_pct: Math.round(((sides.left.amplitude_pct + sides.right.amplitude_pct) / 2) * 10) / 10,
    balance_s: Math.round(mm.stableS * 10) / 10, balance_target_s: Math.round(mm.totalS * 10) / 10, steps: ok, smooth: ok ? Math.round((mm.smooth / ok) * 1000) / 10 : 0,
    sides, moves: [{ key: "vr_seed_reach", reps: n, attempts, successes: ok }], input_source: all.some((r) => r.how === "webxr") ? "webxr_quest_controllers" : "desktop_pointer_preview",
  };
};
